import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

import { gray40, gray80, tertiaryButtonBackgroundColor } from '../../../constants/colors';
import { boldMd, regularLg, regularXl, regularMd } from '../../../constants/location';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 削除しようとした項目の名前 */
  itemName: string;
  /** 削除をブロックしている参照の表示文字列（formatItemReferences の戻り値） */
  references: string[];
};

const styles = {
  closeButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: '100%',
  },
  closeButtonText: {
    ...boldMd,
    color: gray80,
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      borderRadius: '8px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: '24px',
      width: '560px',
      maxWidth: '90vw',
      '@media screen and (max-width: 960px)': {
        padding: '24px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  message: {
    ...regularMd,
    marginBottom: '16px',
  },
  referenceList: {
    ...regularMd,
    marginBottom: '16px',
    paddingLeft: '20px',
  },
  note: {
    ...regularMd,
    marginBottom: '32px',
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
};

/**
 * 照合キーとして使われている項目の削除をブロックする案内モーダル（#3344）。
 *
 * 照合キーが欠けたマッピングは照合処理が成立しないため、システムが勝手にマッピングを
 * 削除するのではなく、利用者にマスタ照合管理画面から設定を外してもらう（仕様書 §2.1・§4.1）。
 * 削除ボタンは出さず、閉じるボタンのみを置く。
 */
const ItemDeleteBlockedModal = ({ open, onClose, itemName, references }: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>項目を削除できません</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.message}>
        {itemName ? `「${itemName}」は` : 'この項目は'}
        以下の設定で照合キーに使われているため削除できません。
      </Typography>
      <Box component="ul" sx={styles.referenceList}>
        {references.map(reference => (
          <Typography component="li" key={reference}>
            {reference}
          </Typography>
        ))}
      </Box>
      <Typography sx={styles.note}>
        先にマスタ照合管理画面から該当の設定を削除してください。
      </Typography>
      <Button sx={styles.closeButton} onClick={onClose}>
        <Typography sx={styles.closeButtonText}>閉じる</Typography>
      </Button>
    </Dialog>
  );
};

export default ItemDeleteBlockedModal;
